import { ClaimStatuses } from '@/types/claim'
import type { DateStr } from '@/types/general'
import type { ProductType } from '@/types/productTypes'

export interface Authenticated {
  authenticated: boolean
}

export interface MeToken {
  token: string
}

export interface Me {
  uuid: string
  firstName: string
  lastName: string
  email: string
  locale: string
  gender: string | null
  mobilePhone: string | null
  birthdate: DateStr | null
  createdAt: DateStr
  mobileDevice: boolean
  clientCardUrl: string
  pendingActions: Array<PendingAction | PendingClaim>
  anyContract: boolean
  anyActiveMotorContract: boolean
  address: {
    street: string | null
    streetNumber: string | null
    streetBox: string | null
    zipCode: string | null
    city: string | null
    country: string | null
  } | null
  unfit: boolean
  pca: Pca | null
  portfolioId: Portfolio | null
  fromPortfolioId: Portfolio | null
  b2bOnboarded?: boolean
  b2cOnboarded: boolean
}

export interface Pca {
  pcaFirstName: string
  pcaLastName: string
  pcaPictureUrl: string
  pcaEmail: string
  pcaTeam: string | null
}

export interface Company {
  pca: Pca | null
  anyActiveMotorContract: boolean
  anyContract: boolean
  email: string
  pendingActions: PendingClaim[]
  vatNumber: string
  companyName: string
  legalForm: string
  entityType: EntityType
}

export interface CompanyMember {
  clientId: string
  firstName: string
  lastName: string
  title: string
  positions: string[]
  roles: MemberRole[]
  email: string
}

export enum Portfolio {
  Marsh = 'marsh',
  Yago = 'yago',
  Cr2000 = 'stroobants_cr2000',
  TanguyStroobants = 'stroobants_ts',
  QuentinDeBrabant = 'stroobants_qdb'
}

export const StroobantsPortfolioIds = [Portfolio.Cr2000, Portfolio.TanguyStroobants, Portfolio.QuentinDeBrabant]

export interface NotificationCount {
  total: number
  b2c: number
  b2b: number
}

export interface PendingAction {
  link: string
  uuid: string
  type: string
  productType: ProductType
  title: string
}

export interface ApiToken {
  token: string
}

export interface PendingClaim {
  aliases: string[]
  claimId: string
  contractNumber: string
  productLine: {
    fr: string
    nl: string
    en: string
  } | null
  productType: ProductType
  insurerReferences: string[]
  settlementAmount: number | null
  status: ClaimStatuses
  type: PendingType.Claim
  statusDate: string
  incidentDate: string
  licencePlate: string | null
}

export enum PendingType {
  Claim = 'claim',
  ContractRequest = 'contract_request',
  Document = 'document',
  Signature = 'signing',
  Unpaid = 'unpaid',
  Offer = 'offer'
}

export interface Profile {
  profileType: ProfileType.B2B | ProfileType.B2C
  email: string
  firstName: string
  lastName: string
  clientId?: string
  company?: { companyId: string; companyName: string; legalForm: string; entityType: EntityType }
  positions?: string[]
  roles?: MemberRole[]
  title?: string | null
}

export enum ProfileType {
  B2B = 'b2b',
  B2C = 'b2c'
}

export enum MemberRole {
  PrimaryContact = 'primary_contact_member',
  Signatory = 'signatory',
  UnpaidAlertReceiver = 'unpaid_alert_receiver'
}

export enum AuthenticationForm {
  Registration = 'registration',
  Login = 'login'
}

export enum EntityType {
  SoleProprietorship = 'sole_proprietorship',
  Company = 'company'
}
